import { useState, useMemo } from "react"

export type SortDir = "asc" | "desc"

interface UseTableSortOptions<T> {
  rows: T[]
  /** Keys whose values should be compared as numbers rather than strings. */
  numericKeys: (keyof T)[]
}

interface UseTableSortResult<T> {
  sortKey: keyof T | null
  sortDir: SortDir
  handleSort: (key: keyof T) => void
  /** Apply sort to a pre-filtered array. Does not filter — pass the filtered
   *  rows in and get back a sorted copy. Returns the original array reference
   *  when no sort key is active (no unnecessary allocation). */
  sortRows: (filtered: T[]) => T[]
}

export function useTableSort<T extends object>({
  rows: _rows,
  numericKeys,
}: UseTableSortOptions<T>): UseTableSortResult<T> {
  const [sortKey, setSortKey] = useState<keyof T | null>(null)
  const [sortDir, setSortDir] = useState<SortDir>("asc")

  const handleSort = (key: keyof T) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"))
    } else {
      setSortKey(key)
      setSortDir("asc")
    }
  }

  const sortRows = useMemo(() => {
    return (filtered: T[]): T[] => {
      if (!sortKey) return filtered
      return [...filtered].sort((a, b) => {
        // Cast to a plain record for dynamic key access — the sort comparator
        // works on runtime values, so the unknown type is intentional here.
        const ra = a as Record<string, unknown>
        const rb = b as Record<string, unknown>
        const key = sortKey as string
        const isNumeric = numericKeys.includes(sortKey)
        const va = isNumeric ? Number(ra[key]) : String(ra[key] ?? "")
        const vb = isNumeric ? Number(rb[key]) : String(rb[key] ?? "")
        if (typeof va === "number" && typeof vb === "number") {
          return sortDir === "asc" ? va - vb : vb - va
        }
        // Numeric collation so strings with embedded numbers sort naturally:
        // STRs order 1-, 2-, … 10- instead of the lexicographic 1-, 10-, 2-.
        return sortDir === "asc"
          ? String(va).localeCompare(String(vb), undefined, { numeric: true })
          : String(vb).localeCompare(String(va), undefined, { numeric: true })
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortKey, sortDir])

  return { sortKey, sortDir, handleSort, sortRows }
}
